import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Loader2, Trash2, Mail, Phone, MapPin, RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "../data/site";
import { getToken, clearToken, login, fetchMe, fetchBookings, updateBookingStatus, deleteBooking } from "../lib/adminApi";

const STATUSES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "booked", label: "Booked" },
  { key: "archived", label: "Archived" },
];
const STATUS_STYLE = {
  new: "bg-[#C26D5C] text-[#F9F8F6]",
  contacted: "bg-[#D1DEC9] text-[#1A362D]",
  booked: "bg-[#1A362D] text-[#F9F8F6]",
  archived: "bg-[#EAE6DF] text-[#7B8A83]",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast.success("Welcome back");
      onLogin(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border border-[#4A5A53] rounded-xl px-4 py-3.5 text-[#F9F8F6] placeholder:text-[#7B8A83] focus:outline-none focus:border-[#C26D5C] focus:ring-1 focus:ring-[#C26D5C] transition-colors duration-200";

  return (
    <div className="min-h-screen bg-[#1A362D] grain relative flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <span className="w-2.5 h-2.5 rounded-full bg-[#C26D5C]" />
          <span className="font-serif text-2xl text-[#F9F8F6]">{COMPANY.name}</span>
        </div>
        <div className="inline-flex items-center gap-2 text-[#D1DEC9] text-xs tracking-[0.2em] uppercase mb-4">
          <Lock size={13} /> Private admin
        </div>
        <h1 className="font-serif font-light text-5xl text-[#F9F8F6] tracking-tight leading-none mb-8">Bookings Inbox</h1>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <input data-testid="admin-email" type="email" className={field} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={field} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button data-testid="admin-login-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C26D5C] text-[#F9F8F6] px-7 py-4 text-sm font-medium hover:bg-[#A85D4E] transition-colors duration-300 disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : null}
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function Inbox({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      setBookings(await fetchBookings());
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error("Could not load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: bookings.length };
    STATUSES.forEach((s) => (c[s.key] = bookings.filter((b) => (b.status || "new") === s.key).length));
    return c;
  }, [bookings]);

  const visible = filter === "all" ? bookings : bookings.filter((b) => (b.status || "new") === filter);

  const changeStatus = async (b, status) => {
    const prev = bookings;
    setBookings((cur) => cur.map((x) => (x.id === b.id ? { ...x, status } : x)));
    try {
      await updateBookingStatus(b.id, status);
    } catch {
      setBookings(prev);
      toast.error("Failed to update status");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete request from ${b.name}? This can't be undone.`)) return;
    const prev = bookings;
    setBookings((cur) => cur.filter((x) => x.id !== b.id));
    try {
      await deleteBooking(b.id);
      toast.success("Request deleted");
    } catch {
      setBookings(prev);
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="sticky top-0 z-30 bg-[#F9F8F6]/90 backdrop-blur-xl border-b border-[#D5D1C8]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#C26D5C]" />
            <span className="font-serif text-xl text-[#1A362D]">{COMPANY.name}</span>
            <span className="hidden sm:inline text-xs tracking-[0.2em] uppercase text-[#7B8A83] ml-3">Bookings Inbox</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-[#D5D1C8] px-4 py-2.5 text-sm text-[#1A362D] hover:bg-[#EAE6DF] transition-colors duration-200">
              <RefreshCw size={15} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-[#1A362D] text-[#F9F8F6] px-4 py-2.5 text-sm hover:bg-[#C26D5C] transition-colors duration-200">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <h1 className="font-serif font-light text-5xl md:text-6xl text-[#1A362D] tracking-tight">Quote requests</h1>
          <div className="flex flex-wrap gap-2" data-testid="admin-filters">
            {[{ key: "all", label: "All" }, ...STATUSES].map((s) => (
              <button
                key={s.key}
                data-testid={`filter-${s.key}`}
                onClick={() => setFilter(s.key)}
                className={`rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${filter === s.key ? "bg-[#1A362D] text-[#F9F8F6] border-[#1A362D]" : "bg-transparent text-[#4A5A53] border-[#D5D1C8] hover:border-[#1A362D]"}`}
              >
                {s.label} <span className="opacity-60">({counts[s.key] ?? 0})</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#7B8A83] py-20 justify-center"><Loader2 className="animate-spin" size={20} /> Loading requests...</div>
        ) : visible.length === 0 ? (
          <div data-testid="admin-empty" className="text-center py-24 border border-dashed border-[#D5D1C8] rounded-2xl">
            <p className="font-serif text-3xl text-[#1A362D]">No requests here yet</p>
            <p className="text-[#7B8A83] mt-2">New quote requests will appear the moment they come in.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-booking-list">
            <AnimatePresence>
              {visible.map((b) => (
                <motion.div
                  key={b.id}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  data-testid={`booking-card-${b.ref}`}
                  className="rounded-2xl border border-[#D5D1C8] bg-white p-6 md:p-7"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-serif text-2xl text-[#1A362D]">{b.name}</h3>
                        <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_STYLE[b.status || "new"]}`}>{(b.status || "new").toUpperCase()}</span>
                        <span className="text-xs text-[#7B8A83]">{b.ref}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-[#4A5A53]">
                        <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1.5 hover:text-[#C26D5C] transition-colors"><Mail size={14} /> {b.email}</a>
                        <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#C26D5C] transition-colors"><Phone size={14} /> {b.phone}</a>
                        <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {b.suburb}</span>
                      </div>
                    </div>
                    <span className="text-xs text-[#7B8A83] whitespace-nowrap">{fmtDate(b.created_at)}</span>
                  </div>

                  {b.services?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {b.services.map((s) => (
                        <span key={s} className="text-xs px-3 py-1 rounded-full bg-[#EAE6DF] text-[#1A362D]">{s}</span>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4A5A53]">
                    {b.frequency && <span><span className="text-[#7B8A83]">Frequency:</span> {b.frequency}</span>}
                    {b.hours && <span><span className="text-[#7B8A83]">Hours:</span> {b.hours}</span>}
                  </div>

                  {b.message && <p className="mt-3 text-[#4A5A53] bg-[#F9F8F6] border border-[#EAE6DF] rounded-xl p-4">{b.message}</p>}

                  <div className="mt-5 pt-5 border-t border-[#EAE6DF] flex items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-1.5">
                      {STATUSES.map((s) => (
                        <button
                          key={s.key}
                          data-testid={`set-status-${s.key}-${b.ref}`}
                          onClick={() => changeStatus(b, s.key)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${(b.status || "new") === s.key ? "bg-[#1A362D] text-[#F9F8F6] border-[#1A362D]" : "border-[#D5D1C8] text-[#4A5A53] hover:border-[#1A362D]"}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <button data-testid={`delete-${b.ref}`} onClick={() => remove(b)} className="inline-flex items-center gap-1.5 text-sm text-[#7B8A83] hover:text-[#C26D5C] transition-colors">
                      <Trash2 size={15} /> Delete
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    fetchMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const logout = () => { clearToken(); setAuthed(false); };

  if (authed === null) {
    return <div className="min-h-screen bg-[#1A362D] flex items-center justify-center"><Loader2 className="animate-spin text-[#F9F8F6]" /></div>;
  }
  return authed ? <Inbox onLogout={logout} /> : <LoginView onLogin={() => setAuthed(true)} />;
}
